import React, { useState } from 'react';
import axios from 'axios';

import CardDialog from '~/components/CardDialog';
import AlertBanner from '~/components/core/AlertBanner';
import Button from '~/components/core/Atomic/Buttons/Button';
import TextField from '~/components/core/Molecules/Fields/TextField';
import FileDropZone from '~/components/FileDropZone';
import type { PromotionPreviewResult } from '~/server_shared/openapi/data-contracts';
import { reportAxiosError } from '~/Utils';

import PromotionChangeSelector from './PromotionChangeSelector';
import PromotionExportBlockedBanner from './PromotionExportBlockedBanner';
import PromotionNewMembersStep from './PromotionNewMembersStep';
import PromotionReasonsNote from './PromotionReasonsNote';
import type { PromotionDanglingReferences, PromotionExportBlocked } from './promotionsUtils';
import {
  getInvalidArtifactMessage,
  getPromotionDanglingReferences,
  getPromotionExportBlocked,
  getPromotionImmutableFieldChangedMessage,
  isPendingPromotionExistsError,
  PENDING_PROMOTION_EXISTS_MESSAGE,
  PROMOTION_DANGLING_REFERENCE_TITLE,
  PROMOTION_IMMUTABLE_FIELD_CHANGED_TITLE,
  promotionsBaseRoute,
} from './promotionsUtils';
import type { PromotionBlockedSection, PromotionChange } from './types';
import { useNewMemberStep } from './useNewMemberStep';

interface CreateUploadPromotionDialogProps {
  open: boolean;
  organizationId: number;
  onClose: () => void;
  onCreated: () => void;
}

/** Upload-source create flow (internal-only): drop a configuration export ZIP or a single-type config JSON,
 * preview it presence-scoped against this org's live config, then submit the selected subset. The artifact
 * is the *source* and the caller's org is the *target* (same DB), so the created request lands in this org's
 * Incoming list and is approved/applied through the existing incoming path. Mirrors the ZIP-import upload
 * half of `ImportDocumentTemplatesZip` for the drop zone, and reuses `PromotionChangeSelector` for the
 * preview → select → submit body. */
const CreateUploadPromotionDialog: React.FC<CreateUploadPromotionDialogProps> = ({
  open,
  organizationId,
  onClose,
  onCreated,
}) => {
  const [file, setFile] = useState<File | undefined>(undefined);
  const [preview, setPreview] = useState<PromotionPreviewResult | null>(null);
  const [selectedChangeIds, setSelectedChangeIds] = useState<string[]>([]);
  // The required, human-readable promotion name captured at create time. `isNameDirty` gates the inline
  // "required" error so it shows only after the user has touched the field, not on first open.
  const [requestName, setRequestName] = useState('');
  const [isNameDirty, setIsNameDirty] = useState(false);
  // True while one or more selected changes are hidden by the change selector's active scope filters — Submit
  // is blocked until the user clears the filters or deselects the hidden changes (reported by the selector).
  const [submitBlocked, setSubmitBlocked] = useState(false);
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // The uploaded artifact was rejected (422: an InvalidArtifactError, or PROMOTION_IMMUTABLE_FIELD_CHANGED when it
  // sets a field a promotion can't change differently from this org) — operator-fixable, shown by the drop zone
  // rather than the generic error modal.
  const [artifactError, setArtifactError] = useState<{ title: string; message: string } | null>(null);
  // A pending request already occupies this target's one slot (shared across upload + live cross-org
  // sources). Inline + non-destructive: keep the preview so the user can resubmit once it clears.
  const [pendingExists, setPendingExists] = useState(false);
  // A 422 PROMOTION_EXPORT_BLOCKED on preview (the caller IS the target here) — operator-fixable, listed per
  // offender in-dialog rather than through the error modal / Sentry. A blocked section of this org's export is
  // normally left out of the preview instead (`blockedSections`).
  const [exportBlocked, setExportBlocked] = useState<PromotionExportBlocked | null>(null);
  // A 422 PROMOTION_DANGLING_REFERENCE on preview: the file's changes name items this org doesn't have and the file
  // doesn't create. The file itself is valid, so this is listed per reference in-dialog, not by the drop zone.
  const [danglingReferences, setDanglingReferences] = useState<PromotionDanglingReferences | null>(null);
  // Sections the preview left out because this org's export of them is blocked. Taken from the initial
  // preview only: a refine re-diffs the same pinned exports and does not re-send the reasons.
  const [blockedSections, setBlockedSections] = useState<PromotionBlockedSection[]>([]);

  const baseRoute = promotionsBaseRoute(organizationId);

  // Shared interim "New members" step (opt-in + refine), identical to the live cross-org create flow.
  const memberStep = useNewMemberStep({
    preview,
    setPreview,
    baseRoute,
    resetSelection: () => {
      setSelectedChangeIds([]);
      setSubmitBlocked(false);
    },
  });

  const resetAndClose = () => {
    setFile(undefined);
    setPreview(null);
    setSelectedChangeIds([]);
    setSubmitBlocked(false);
    setArtifactError(null);
    setPendingExists(false);
    setExportBlocked(null);
    setDanglingReferences(null);
    setBlockedSections([]);
    setRequestName('');
    setIsNameDirty(false);
    memberStep.resetMembers();
    onClose();
  };

  const handleFileSelect = (selected: File) => {
    setFile(selected);
    setPreview(null);
    setSelectedChangeIds([]);
    setSubmitBlocked(false);
    setArtifactError(null);
    setPendingExists(false);
    setExportBlocked(null);
    setDanglingReferences(null);
    setBlockedSections([]);
  };

  const handleCancelFileSelect = () => {
    setFile(undefined);
    setPreview(null);
    setSelectedChangeIds([]);
    setSubmitBlocked(false);
    setArtifactError(null);
    setExportBlocked(null);
    setDanglingReferences(null);
    setBlockedSections([]);
  };

  const handlePreview = async () => {
    if (!file) {
      return;
    }
    try {
      setIsPreviewing(true);
      setPreview(null);
      // Drop the prior preview's selection so the footer Submit can't stay enabled from a stale subset
      // during the paint before the selector re-reports the new preview's full selection.
      setSelectedChangeIds([]);
      setSubmitBlocked(false);
      setArtifactError(null);
      setPendingExists(false);
      setExportBlocked(null);
      setDanglingReferences(null);
      setBlockedSections([]);
      const formData = new FormData();
      formData.append('file', file, file.name);
      const { data } = await axios.post<PromotionPreviewResult>(`${baseRoute}/upload/preview`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setPreview(data);
      setBlockedSections(data.blocked_sections ?? []);
      memberStep.seedPromotedMembers(data);
    } catch (error) {
      // Classify the marked 422s (blocked export, dangling reference, immutable field) first:
      // getInvalidArtifactMessage matches ANY 422, so the order is what keeps each from being misread as a bad
      // uploaded file.
      const blocked = getPromotionExportBlocked(error);
      const dangling = blocked ? null : getPromotionDanglingReferences(error);
      const immutableField = blocked || dangling ? null : getPromotionImmutableFieldChangedMessage(error);
      const invalidArtifact = blocked || dangling || immutableField ? null : getInvalidArtifactMessage(error);
      if (blocked) {
        setExportBlocked(blocked);
      } else if (dangling) {
        setDanglingReferences(dangling);
      } else if (immutableField) {
        setArtifactError({ title: PROMOTION_IMMUTABLE_FIELD_CHANGED_TITLE, message: immutableField });
      } else if (invalidArtifact) {
        setArtifactError({ title: 'Invalid configuration file', message: invalidArtifact });
      } else {
        await reportAxiosError(error);
      }
    } finally {
      setIsPreviewing(false);
    }
  };

  const handleSubmit = async () => {
    if (!preview || selectedChangeIds.length === 0 || !requestName.trim()) {
      return;
    }
    try {
      setIsSubmitting(true);
      setPendingExists(false);
      await axios.post(`${baseRoute}/upload`, {
        preview_token: preview.preview_token,
        selected_change_ids: selectedChangeIds,
        request_name: requestName.trim(),
      });
      onCreated();
      resetAndClose();
    } catch (error) {
      if (isPendingPromotionExistsError(error)) {
        // The target's one pending slot is taken. Keep the preview/selection so the user can resubmit once
        // it clears; no error modal / Sentry.
        setPendingExists(true);
      } else {
        // Preview drift/expiry/stale selection (409) or another error — re-upload to refresh.
        await reportAxiosError(error);
        setPreview(null);
        setBlockedSections([]);
        setSelectedChangeIds([]);
        setSubmitBlocked(false);
        memberStep.resetMembers();
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const { showMemberStep, showChangeSelector } = memberStep;

  return (
    <CardDialog
      isDialog
      title="Promote from file"
      open={open}
      onClose={resetAndClose}
      preventClose={isSubmitting || memberStep.isRefining}
      fullWidth
      maxWidth="md"
      footerActions={
        <div className="flex items-center gap-x-12">
          <Button variant="text" onClick={resetAndClose}>
            Cancel
          </Button>
          {!preview ? (
            <Button variant="contained" color="primary" disabled={!file || isPreviewing} onClick={handlePreview}>
              {isPreviewing ? 'Previewing…' : 'Preview changes'}
            </Button>
          ) : showMemberStep ? (
            <Button
              variant="contained"
              color="primary"
              disabled={memberStep.isRefining}
              onClick={memberStep.handleConfirmMembers}
            >
              {memberStep.isRefining ? 'Loading…' : 'Continue to review'}
            </Button>
          ) : (
            <Button
              variant="contained"
              color="primary"
              disabled={selectedChangeIds.length === 0 || isSubmitting || submitBlocked || !requestName.trim()}
              onClick={handleSubmit}
            >
              {isSubmitting ? 'Submitting…' : 'Submit promotion'}
            </Button>
          )}
        </div>
      }
    >
      <div className="flex flex-col gap-16 py-8">
        <FileDropZone
          uploadPercent={0}
          file={file}
          onFileSelect={handleFileSelect}
          onCancelFileSelect={handleCancelFileSelect}
          error={Boolean(artifactError)}
          errorMessage={artifactError?.message}
          dropZoneOptions={{ accept: '.zip,.json', disabled: Boolean(preview) }}
          disabled={Boolean(preview)}
        />

        {/* Two-step flow: first screen picks the source artifact; the required Name is only asked for once
            the preview (the change list) has rendered, at which point the artifact is locked so it can't
            drift out from under a captured name/selection. */}
        {showChangeSelector && (
          <TextField
            label="Name"
            size="medium"
            value={requestName}
            onChange={(newValue: string) => {
              setRequestName(newValue);
              setIsNameDirty(true);
            }}
            error={isNameDirty && !requestName.trim()}
            helperText={isNameDirty && !requestName.trim() ? 'A name is required' : ''}
            fullWidth
            inputProps={{ maxLength: 256 }}
          />
        )}

        {artifactError && (
          <AlertBanner
            withIcon
            alertType={AlertBanner.ALERT_TYPES.ERROR}
            title={artifactError.title}
            note={artifactError.message}
          />
        )}

        {exportBlocked && <PromotionExportBlockedBanner exportBlocked={exportBlocked} />}

        {danglingReferences && (
          <AlertBanner
            withIcon
            alertType={AlertBanner.ALERT_TYPES.ERROR}
            title={PROMOTION_DANGLING_REFERENCE_TITLE}
            note={<PromotionReasonsNote message={danglingReferences.message} reasons={danglingReferences.reasons} />}
          />
        )}

        {showMemberStep && preview && (
          <PromotionNewMembersStep
            newMembers={preview.new_members ?? { lobs: [], sub_organizations: [] }}
            changes={(preview.changes ?? []) as PromotionChange[]}
            targetName="this organization"
            promotedLobIds={memberStep.promotedLobIds}
            promotedSubOrgIds={memberStep.promotedSubOrgIds}
            onToggleMember={memberStep.handleToggleMember}
          />
        )}

        {showChangeSelector && preview && (
          <PromotionChangeSelector
            previewResult={preview}
            onSelectionChange={setSelectedChangeIds}
            onSubmitBlockedChange={setSubmitBlocked}
            promotedLobIds={memberStep.promotedLobIds}
            promotedSubOrgIds={memberStep.promotedSubOrgIds}
            blockedSections={blockedSections}
            banner={
              pendingExists && (
                <AlertBanner
                  withIcon
                  alertType={AlertBanner.ALERT_TYPES.WARNING}
                  title="Promotion already pending for this target"
                  note={PENDING_PROMOTION_EXISTS_MESSAGE}
                />
              )
            }
          />
        )}
      </div>
    </CardDialog>
  );
};

export default CreateUploadPromotionDialog;
